/**
 * Pushing an edited order line's customs fields back onto the contents it generated.
 *
 * The link is content.Id === orderLine.Id, set once at generation time by linesToContents. It is
 * deliberately not one-to-one: padding a short order line out across parcels, and splitting its
 * units, both copy the Id, so one line can own rows in several parcels. Rows with Id == null are
 * skipped by design, not defensively - addManualContent and replicateIntoParcels null it precisely
 * because those rows have no order line behind them and must not be overwritten by one.
 *
 * WHY A THREE-WAY MERGE, not a straight overwrite. Once a consignment exists the parcels are what
 * gets declared, and the user can edit any row directly in the parcel editor. A row still holding
 * the line's PRE-EDIT value was clearly never touched there, so carrying the line's new value onto
 * it is just finishing a job the user started. A row holding anything else was customised on
 * purpose, and an order line edit has no business undoing that. Base is therefore the pre-edit line
 * value, and the test is per row - one line's rows can partly update and partly hold their own.
 *
 * That rule is what makes Description safe to include. Under 'pad-with-copies' every parcel starts
 * with a copy of the same row, so a description edit reaches all of them; the moment the user
 * re-describes one box - which that overflow contract explicitly expects - that box drops out and
 * the rest still update.
 *
 * Planning and applying are separate calls because the UI needs the answer on two different
 * renders: once to decide whether to offer the change and name it, once when the user accepts.
 * Re-deriving the diff inside apply would let the message and the write disagree.
 */
import {
  ConsignmentApiModel,
  ConsignmentParcelItemApiModel,
  MarketplaceOrderApiModel
} from '~/lib/api/smartsend/types';
import { MapMarketplaceOrderlineToConsignmentParcel } from '../../components/smartsend/grouping/MapMarketplaceOrderlineToConsignmentParcel';

type MappedField = keyof ReturnType<typeof MapMarketplaceOrderlineToConsignmentParcel>;

/**
 * Fields a line owns outright, so a change to one can be carried onto the rows it generated.
 * Adding a field is a one-line edit here, PROVIDED the mapper above already emits it.
 *
 * Quantity is excluded by the type, and Value is not in the mapper's output at all. Neither may
 * ever be added: both are ALLOCATED across parcels rather than copied to them (distributeIntoParcels,
 * flattenToUnits, splitValueAcross), so writing a line total onto every matching row would multiply
 * the declared amount by the number of parcels holding that line.
 */
export const SYNCABLE_CONTENT_FIELDS = [
  'Description',
  'Sku',
  'TariffCode',
  'CountryOfManufacture'
] as const satisfies readonly Exclude<MappedField, 'Quantity'>[];

export type SyncableContentField = (typeof SYNCABLE_CONTENT_FIELDS)[number];

/** One field of one line that has moved away from the baseline, counted against the rows it owns. */
export interface OrderLineFieldChange {
  lineId: string;
  field: SyncableContentField;
  /** Normalised value the line held before the edit - the merge base. */
  from: string;
  /** Normalised value the line holds now. */
  to: string;
  /** Matching rows still holding `from`, so applying would write to them. */
  eligible: number;
  /** Matching rows holding neither value - customised in the parcel editor, left alone. */
  diverged: number;
  /** Matching rows already holding `to`, so there is nothing to do for them. */
  already: number;
}

export interface OrderLineSyncPlan {
  /** Changes with at least one eligible row. Non-empty is exactly "there is something to offer". */
  pending: OrderLineFieldChange[];
  /** Changed, but every matching row was customised. Worth explaining; nothing to offer. */
  blocked: OrderLineFieldChange[];
  /** Distinct fields across `pending`, in SYNCABLE_CONTENT_FIELDS order, for the message. */
  fields: SyncableContentField[];
  /** True when applying would knowingly leave at least one matching row alone. */
  partial: boolean;
}

export interface OrderLineSyncResult {
  /** The input object by identity when nothing applied, so an identity check is enough. */
  consignment: ConsignmentApiModel;
  applied: boolean;
  updatedRows: number;
  /** Matching rows deliberately left alone because they had been customised. */
  skippedRows: number;
}

/**
 * Content fields are optional, so undefined and '' mean the same thing and must compare equal - an
 * imported line with no tariff code against a row holding '' is unchanged, not a pending edit.
 * Nothing else is normalised: trimming or case-folding here would silently swallow a real edit.
 */
const norm = (value?: string): string => value ?? '';

const lineValue = (line: MarketplaceOrderApiModel, field: SyncableContentField): string =>
  norm(MapMarketplaceOrderlineToConsignmentParcel(line)[field]);

/** Every row the given order line owns. Contents can be undefined, hence the `?? []`. */
const rowsForLine = (consignment: ConsignmentApiModel, lineId: string): ConsignmentParcelItemApiModel[] =>
  consignment.Parcels.flatMap((parcel) => (parcel.Contents ?? []).filter((row) => row.Id != null && row.Id === lineId));

const emptyPlan = (): OrderLineSyncPlan => ({ pending: [], blocked: [], fields: [], partial: false });

/**
 * Works out which order line edits can be carried onto the consignment, without changing anything.
 *
 * `baselineLines` is the state the lines were in when the consignment last agreed with them - NOT
 * necessarily the originally imported lines. It has to advance every time the two are reconciled
 * (on apply, and whenever the parcels are regenerated from the lines), or a second edit to the same
 * field would compare against a value no row holds any more and nothing would ever be eligible again.
 */
export const planOrderLineSync = (
  consignment: ConsignmentApiModel | null,
  baselineLines: MarketplaceOrderApiModel[],
  currentLines: MarketplaceOrderApiModel[]
): OrderLineSyncPlan => {
  if (!consignment?.Parcels?.length || !currentLines?.length) {
    return emptyPlan();
  }

  const pending: OrderLineFieldChange[] = [];
  const blocked: OrderLineFieldChange[] = [];

  currentLines.forEach((line) => {
    const baseline = baselineLines?.find((b) => b.Id != null && b.Id === line.Id);

    if (!baseline) {
      return;
    }

    const rows = rowsForLine(consignment, line.Id);

    if (rows.length === 0) {
      return;
    }

    SYNCABLE_CONTENT_FIELDS.forEach((field) => {
      const from = lineValue(baseline, field);
      const to = lineValue(line, field);

      if (from === to) {
        return;
      }

      const change: OrderLineFieldChange = {
        lineId: line.Id,
        field,
        from,
        to,
        eligible: rows.filter((row) => norm(row[field]) === from).length,
        already: rows.filter((row) => norm(row[field]) === to).length,
        diverged: rows.filter((row) => norm(row[field]) !== from && norm(row[field]) !== to).length
      };

      if (change.eligible > 0) {
        pending.push(change);
      } else if (change.diverged > 0) {
        blocked.push(change);
      }
    });
  });

  return {
    pending,
    blocked,
    fields: SYNCABLE_CONTENT_FIELDS.filter((field) => pending.some((c) => c.field === field)),
    partial: pending.some((c) => c.diverged > 0)
  };
};

/**
 * Carries out exactly what the plan described.
 *
 * Takes the plan rather than the lines so the write cannot differ from what the user was shown, and
 * so it can be tested against a hand-written plan. Deliberately does NOT call
 * ConsolidateParcelQuantities: applying can leave two rows in one parcel identical, but merging them
 * mid-edit would make rows disappear from under the user. SanitiseConsignmentForSave merges them at
 * save time instead, conserving quantity and value.
 */
export const applyOrderLineSync = (consignment: ConsignmentApiModel, plan: OrderLineSyncPlan): OrderLineSyncResult => {
  if (!consignment?.Parcels?.length || !plan?.pending?.length) {
    return { consignment, applied: false, updatedRows: 0, skippedRows: 0 };
  }

  let updatedRows = 0;
  let skippedRows = 0;

  const Parcels = consignment.Parcels.map((parcel) => ({
    ...parcel,
    Contents: (parcel.Contents ?? []).map((row) => {
      if (row.Id == null) {
        return row;
      }

      const writes = plan.pending.filter((c) => c.lineId === row.Id);

      if (writes.length === 0) {
        return row;
      }

      const patch: Partial<Record<SyncableContentField, string>> = {};
      let skipped = false;

      writes.forEach((change) => {
        if (norm(row[change.field]) === change.from) {
          patch[change.field] = change.to;
        } else if (norm(row[change.field]) !== change.to) {
          skipped = true;
        }
      });

      if (skipped) {
        skippedRows++;
      }

      if (Object.keys(patch).length === 0) {
        return row;
      }

      updatedRows++;
      return { ...row, ...patch };
    })
  }));

  return updatedRows > 0
    ? { consignment: { ...consignment, Parcels }, applied: true, updatedRows, skippedRows }
    : { consignment, applied: false, updatedRows: 0, skippedRows };
};

/**
 * Whether every row still matches the order line it was generated from, so rebuilding the parcels
 * from the order would lose nothing.
 *
 * The parcel-count picker uses this to decide whether it may offer counts above what redistribution
 * can build. Going above them rebuilds from the order, which is the bundled equivalent of picking 1x
 * and then the new count - free when nothing has been customised, destructive when it has.
 *
 * Quantity and Value are excluded for the same reason SYNCABLE_CONTENT_FIELDS excludes them: both are
 * ALLOCATED across parcels, so a split or padded row legitimately holds neither the line's total nor
 * an equal share of it. A row is judged only on the fields a line owns outright.
 */
export const consignmentIsPristine = (
  consignment: ConsignmentApiModel | null,
  lines: MarketplaceOrderApiModel[]
): boolean => {
  const rows = (consignment?.Parcels ?? []).flatMap((parcel) => parcel.Contents ?? []);

  if (rows.length === 0 || !lines?.length) {
    return false;
  }

  return rows.every((row) => {
    // A Manual row has no line behind it, and a row whose line has since left the order has nothing
    // left to rebuild from. Either way regenerating would drop it.
    if (row.Manual || row.Id == null) {
      return false;
    }

    const line = lines.find((l) => l.Id != null && l.Id === row.Id);

    // Product guarded because the mapper dereferences it, and this runs on every render of the
    // parcel-count picker - a CSV-derived or partially hydrated line must not throw in there. An
    // unverifiable line counts as not pristine, which just means the count range stays clamped.
    if (!line?.Product) {
      return false;
    }

    return SYNCABLE_CONTENT_FIELDS.every((field) => norm(row[field]) === lineValue(line, field));
  });
};

/**
 * Whether the parcels were rebuilt from the order lines rather than edited in place.
 *
 * Regeneration mints fresh UIDs for every row (linesToContents, flattenToUnits, the pad branch),
 * while every in-place edit in planEdits preserves them - expandContent mints, but only for the row
 * it splits, so the rest still match. So "no row UID survives" is a precise test, not a heuristic.
 *
 * Callers use this to know the baseline is stale: a regenerated plan was built from the CURRENT
 * lines, so the lines and the parcels already agree and the old baseline would strand the diff.
 */
export const contentsWereRegenerated = (
  previous: ConsignmentApiModel | null,
  next: ConsignmentApiModel | null
): boolean => {
  if (!next?.Parcels?.length) {
    return true;
  }

  const nextUIDs = next.Parcels.flatMap((p) => (p.Contents ?? []).map((row) => row.UID));

  if (nextUIDs.length === 0) {
    return true;
  }

  const previousUIDs = new Set((previous?.Parcels ?? []).flatMap((p) => (p.Contents ?? []).map((row) => row.UID)));

  return !nextUIDs.some((uid) => previousUIDs.has(uid));
};
